import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Tabs,
  Tab,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useGetPlayerByIdQuery } from "../store/api/mlbApi";
import { useResolvedPlayerId } from "../hooks/useResolvedPlayerId";
import { buildEchartsOptions } from "../utils/chartTheme";
import { useSavePlayerChart } from "../hooks/useSavePlayerChart";
import { useAnalytics } from "../hooks/useAnalytics";
import { formatPlayerName } from "../utils/textUtils";
import PlayerHeadshot from "../components/PlayerHeadshot";
import PlayerBioSummary from "../components/PlayerBioSummary";
import SimilarSeasons from "../components/similarSeasons/SimilarSeasons";
import {
  HITTING_STATS,
  STAT_CATEGORIES,
  STAT_OPTIONS,
  getStatOptionsByCategory,
  getSeasonColumnsByCategory,
  getStatLabel,
} from "../constants/stats";
import {
  buildSeasonChartPoints,
  seasonChartHasValues,
} from "../utils/playerSeasonChart";
import {
  buildCareerStatRows,
  buildSeasonStatRows,
  enhanceSeasonColumns,
} from "../utils/playerSeasonRows";
import {
  defaultStatCategory,
  isMlbStubPlayerId,
  playerHasDisplayableStats,
  playerStatsEmptyMessage,
} from "../utils/playerStatCoverage";
import {
  eligibleMlbSimilarSeasons,
  parseMlbSimilarSearch,
  serializeMlbSimilarSearch,
} from "../utils/mlbSimilarSeasonsPage";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  formatGridStatValue,
  getResultsGridSx,
  sortedClassFor,
} from "../utils/resultsGrid";

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PlayerDetails = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    playerId,
    isResolving,
    unavailable,
    errorMessage: mlbErrorMessage,
  } = useResolvedPlayerId(id);
  const { data: player, isLoading: loading, error } = useGetPlayerByIdQuery(
    playerId,
    { skip: !playerId }
  );
  const [tabValue, setTabValue] = useState(0); // Default to Stats Graph tab
  const [statCategory, setStatCategory] = useState(null);
  const [statType, setStatType] = useState(STAT_OPTIONS[0].value); // First hitting stat
  const [careerSortModel, setCareerSortModel] = useState([
    { field: "category", sort: "asc" },
  ]);
  const [seasonSortModel, setSeasonSortModel] = useState([
    { field: "year", sort: "desc" },
  ]);

  // Analytics hook
  const { trackPlayerViewed, trackWidgetSaved } = useAnalytics();

  // Track player view for analytics
  useEffect(() => {
    if (player) {
      trackPlayerViewed(
        playerId,
        formatPlayerName(player),
        "player_details_page"
      );
    }
  }, [player, playerId, trackPlayerViewed]);

  // Widget save state
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [widgetTitle, setWidgetTitle] = useState("");
  const [saveStatus, setSaveStatus] = useState({
    show: false,
    success: false,
    message: "",
  });

  // Widget save hook
  const { savePlayerChart } = useSavePlayerChart("home");

  // Use centralized stat options from constants

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setStatCategory(newCategory);

    // Reset to first stat in the new category
    const newStats = getStatOptionsByCategory(newCategory);
    if (newStats && newStats.length > 0) {
      setStatType(newStats[0].value);
    }

    if (
      newCategory === STAT_CATEGORIES.HITTING ||
      newCategory === STAT_CATEGORIES.PITCHING
    ) {
      const { season: currentSeason } = parseMlbSimilarSearch(searchParams);
      const nextEligibleSeasons = eligibleMlbSimilarSeasons(player, newCategory);
      const nextSeason = nextEligibleSeasons.includes(currentSeason)
        ? currentSeason
        : nextEligibleSeasons[0];
      setSearchParams(
        serializeMlbSimilarSearch({ season: nextSeason, role: newCategory })
      );
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("season");
    nextParams.delete("role");
    setSearchParams(nextParams);
  };

  const handleStatChange = (event) => {
    setStatType(event.target.value);
  };

  useEffect(() => {
    setStatCategory(null);
  }, [playerId]);

  const similarSearch = useMemo(
    () => parseMlbSimilarSearch(searchParams),
    [searchParams]
  );
  const preferredCategory = defaultStatCategory(player?.stats);
  const activeCategory = statCategory || similarSearch.role || preferredCategory;
  const currentStatOptions = getStatOptionsByCategory(activeCategory);
  const activeStatType = currentStatOptions.some((option) => option.value === statType)
    ? statType
    : currentStatOptions[0]?.value || statType;
  const hasStatTables = playerHasDisplayableStats(player?.stats);
  const emptyStatsMessage = playerStatsEmptyMessage({
    isMlbStub: isMlbStubPlayerId(playerId),
  });
  const similarSeasonsRole =
    activeCategory === STAT_CATEGORIES.HITTING ||
    activeCategory === STAT_CATEGORIES.PITCHING
      ? activeCategory
      : undefined;
  const similarSeasonOptions = useMemo(
    () => eligibleMlbSimilarSeasons(player, similarSeasonsRole),
    [player, similarSeasonsRole]
  );
  const selectedSimilarSeason = similarSeasonOptions.includes(
    similarSearch.season
  )
    ? similarSearch.season
    : similarSeasonOptions[0];
  const showSimilarSeasons = Boolean(
    similarSeasonsRole && selectedSimilarSeason
  );

  // Use centralized column definitions from constants

  // Widget save functions
  const handleSaveWidget = () => {
    if (!player) {
      setSaveStatus({
        show: true,
        success: false,
        message: "No player data available",
      });
      return;
    }

    // Generate default title
    const defaultTitle = `${formatPlayerName(player)} - ${getStatLabel(activeStatType)}`;
    setWidgetTitle(widgetTitle || defaultTitle);
    setIsSaveDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    try {
      const chartConfig = {
        title: widgetTitle,
        players: [player],
        selectedStat: activeStatType,
        statCategory: activeCategory,
        normalizeByCareer: false, // Single player charts use calendar years
        type: "single",
      };

      const result = await savePlayerChart(chartConfig);

      if (result.success) {
        // Track successful widget save
        trackWidgetSaved(
          "player_chart",
          "single_player",
          activeStatType,
          undefined,
          playerId,
          formatPlayerName(player)
        );

        setSaveStatus({
          show: true,
          success: true,
          message: "Chart saved as widget successfully!",
        });
        setIsSaveDialogOpen(false);
        setWidgetTitle("");
      } else {
        setSaveStatus({
          show: true,
          success: false,
          message: result.error || "Failed to save widget",
        });
      }
    } catch (error) {
      setSaveStatus({
        show: true,
        success: false,
        message: "An error occurred while saving the widget",
      });
    }
  };

  const handleCloseSaveDialog = () => {
    setIsSaveDialogOpen(false);
    setWidgetTitle("");
  };

  const handleCloseSnackbar = () => {
    setSaveStatus({ show: false, success: false, message: "" });
  };

  const seasonChartPoints = buildSeasonChartPoints(
    player?.stats?.[activeCategory]?.seasons,
    activeStatType
  );
  const hasSeasonChartValues = seasonChartHasValues(seasonChartPoints);
  const careerSortedField = careerSortModel[0]?.field;
  const seasonSortedField = seasonSortModel[0]?.field;

  const careerRows = useMemo(
    () => buildCareerStatRows(player?.stats?.[activeCategory]?.career),
    [player, activeCategory]
  );
  const careerColumns = useMemo(
    () => [
      {
        field: "category",
        headerName: "Category",
        flex: 1,
        minWidth: 200,
        headerClassName: sortedClassFor("category", careerSortedField),
        cellClassName: sortedClassFor("category", careerSortedField),
        sortingOrder: NAME_SORT_ORDER,
      },
      {
        field: "value",
        headerName: "Value",
        width: 150,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("value", careerSortedField),
        cellClassName: sortedClassFor("value", careerSortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) =>
          formatGridStatValue(params.value, params.row.format),
      },
    ],
    [careerSortedField]
  );
  const seasonRows = useMemo(
    () => buildSeasonStatRows(player?.stats?.[activeCategory]?.seasons),
    [player, activeCategory]
  );
  const seasonColumns = useMemo(
    () =>
      enhanceSeasonColumns(
        getSeasonColumnsByCategory(activeCategory),
        seasonSortedField
      ),
    [seasonSortedField, activeCategory]
  );

  const getChartOption = () => {
    if (!hasSeasonChartValues) return {};

    return buildEchartsOptions({
      tooltip: {
        trigger: "axis",
        formatter: function (params) {
          const point = params[0]?.data;
          const displayValue =
            point?.value == null ? "—" : point.value;
          return `Year: ${point?.year ?? "—"}<br/>${params[0].seriesName}: ${displayValue}`;
        },
      },
      xAxis: {
        type: "category",
        data: seasonChartPoints.map((point) => point.year),
        name: "Year",
        nameLocation: "middle",
        nameGap: 35,
      },
      yAxis: {
        type: "value",
        name: getStatLabel(activeStatType),
        nameLocation: "middle",
        nameGap: 50,
      },
      series: [
        {
          name: getStatLabel(activeStatType),
          type: "line",
          data: seasonChartPoints,
          smooth: true,
          symbolSize: 8,
        },
      ],
    });
  };

  if (isResolving || loading || (playerId && !player && !error)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (unavailable) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        {mlbErrorMessage}
      </Typography>
    );
  }

  if (error || !player) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        {error?.data?.error || "Player not found"}
      </Typography>
    );
  }

  const { bio, stats } = player;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Player Bio Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="flex-start">
            {/* Player Photo */}
            <Grid item xs={12} sm={3} md={2}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <PlayerHeadshot
                  playerId={playerId}
                  size={150}
                  variant="circular"
                  showTooltip={true}
                  showLoading={true}
                  showError={false}
                />
              </Box>
            </Grid>

            {/* Player Info */}
            <Grid item xs={12} sm={9} md={10}>
              <Typography variant="h4" gutterBottom>
                {formatPlayerName(player)}
              </Typography>
              <PlayerBioSummary bio={bio} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      {!hasStatTables ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {emptyStatsMessage}
        </Alert>
      ) : (
      <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Stats Graph" />
          <Tab label="Career Stats" />
          <Tab label="Season by Season" />
        </Tabs>
      </Box>

      {/* Stats Graph Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
          <FormControl sx={{ width: "25%" }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={activeCategory}
              onChange={handleCategoryChange}
              label="Category"
            >
              <MenuItem value={STAT_CATEGORIES.HITTING}>Hitting</MenuItem>
              <MenuItem value={STAT_CATEGORIES.PITCHING}>Pitching</MenuItem>
              <MenuItem value={STAT_CATEGORIES.FIELDING}>Fielding</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ width: "25%" }}>
            <InputLabel>Select Stat</InputLabel>
            <Select
              value={activeStatType}
              onChange={handleStatChange}
              label="Select Stat"
            >
              {currentStatOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Paper sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {getStatLabel(activeStatType)} by Year
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSaveWidget}
              size="small"
            >
              Save as Widget
            </Button>
          </Box>
          {hasSeasonChartValues ? (
            <ReactECharts option={getChartOption()} style={{ height: "400px" }} />
          ) : (
            <Alert severity="info">
              {activeStatType === HITTING_STATS.STOLEN_BASES
                ? "No season stolen-base totals in this player's log. Almanac-era hitting tables do not include SB; only MLB-synced seasons have them."
                : `No season values for ${getStatLabel(activeStatType)} in this player's log.`}
            </Alert>
          )}
        </Paper>
      </TabPanel>

      {/* Career Stats Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ width: "25%" }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={activeCategory}
              onChange={handleCategoryChange}
              label="Category"
            >
              <MenuItem value={STAT_CATEGORIES.HITTING}>Hitting</MenuItem>
              <MenuItem value={STAT_CATEGORIES.PITCHING}>Pitching</MenuItem>
              <MenuItem value={STAT_CATEGORIES.FIELDING}>Fielding</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Paper sx={{ overflow: "hidden" }}>
          <DataGrid
            rows={careerRows}
            columns={careerColumns}
            loading={false}
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter
            sortingMode="client"
            sortModel={careerSortModel}
            onSortModelChange={setCareerSortModel}
            rowHeight={52}
            sx={getResultsGridSx()}
          />
        </Paper>
      </TabPanel>

      {/* Season by Season Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ width: "25%" }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={activeCategory}
              onChange={handleCategoryChange}
              label="Category"
            >
              <MenuItem value={STAT_CATEGORIES.HITTING}>Hitting</MenuItem>
              <MenuItem value={STAT_CATEGORIES.PITCHING}>Pitching</MenuItem>
              <MenuItem value={STAT_CATEGORIES.FIELDING}>Fielding</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Paper sx={{ overflow: "hidden" }}>
          <DataGrid
            rows={seasonRows}
            columns={seasonColumns}
            loading={false}
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter
            sortingMode="client"
            sortModel={seasonSortModel}
            onSortModelChange={setSeasonSortModel}
            rowHeight={52}
            sx={getResultsGridSx()}
          />
        </Paper>
      </TabPanel>
      {showSimilarSeasons && (
        <Box sx={{ mt: 3 }}>
          <SimilarSeasons
            league="mlb"
            playerId={playerId}
            season={selectedSimilarSeason}
            seasons={similarSeasonOptions}
            role={similarSeasonsRole}
            onSeasonChange={(season) =>
              setSearchParams(
                serializeMlbSimilarSearch({
                  season,
                  role: similarSeasonsRole,
                })
              )
            }
          />
        </Box>
      )}
      </>
      )}

      {/* Save Widget Dialog */}
      <Dialog
        open={isSaveDialogOpen}
        onClose={handleCloseSaveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Chart as Widget</DialogTitle>
        <DialogContent>
          <TextField
            label="Widget Title"
            value={widgetTitle}
            onChange={(e) => setWidgetTitle(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Enter a title for your widget"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={!widgetTitle.trim()}
          >
            Save Widget
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Snackbar */}
      <Snackbar
        open={saveStatus.show}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={saveStatus.success ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {saveStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlayerDetails;
